import { useEffect, useState, useCallback, useMemo, Fragment } from "react";
import {
  Button, Card, Col, Form, Row, Table, Spinner, Badge, Modal,
} from "react-bootstrap";
import {
  FaPlus, FaCheck, FaTimes, FaMoneyBillWave, FaFileInvoiceDollar, FaLink, FaTrash, FaChevronDown, FaChevronRight,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import Select from "react-select";
import { buildChips, countActiveChips, resolveMapLabel } from "./utils/accountingFilterUtils";
import AccountingFilterSection from "./components/AccountingFilterSection";
import AccountingActiveFilters from "./components/AccountingActiveFilters";
import "./accountingFilters.css";
import "./accountingResponsive.css";
import { formatVND as fmtVND, toMoneyNumber } from "./utils/moneyFormat";
import MoneyInput from "./components/MoneyInput";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import axiosInstance from "../../axiosInstance";
import AdLayout from "../../components/AdLayout";
import { Pagination } from "../../components/ui";
import { fetchAccountingPermissions, ACCOUNTING_SO_CHI_ADVANCE } from "../../libs/customerCreatePermission";

const PAGE_SIZE = 20;

const STATUS_MAP = {
  PENDING: { label: "Chờ duyệt", bg: "warning" },
  APPROVED: { label: "Đã duyệt", bg: "info" },
  DISBURSED: { label: "Đã giải ngân", bg: "primary" },
  PENDING_SETTLEMENT: { label: "Chờ tất toán", bg: "primary" },
  SETTLED: { label: "Đã tất toán", bg: "success" },
  REJECTED: { label: "Từ chối", bg: "danger" },
};
const cardStyle = { boxShadow: "0 2px 8px rgba(0,0,0,0.1)" };
const unwrap = (res) => res?.data?.data ?? res?.data ?? {};
const isOk = (res) => res?.data?.success !== false;

const INITIAL_FORM = {
  employeeId: "",
  employeeName: "",
  employeeCode: "",
  departmentName: "",
  divisionName: "",
  position: "",
  amount: "",
  reason: "",
};

const EMPTY_ADVANCE_FILTERS = { status: "" };

const AdvanceList = ({ embedded = false }) => {
  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);

  const [members, setMembers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [salaryConfigs, setSalaryConfigs] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  const [filterStatus, setFilterStatus] = useState("");
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_ADVANCE_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);

  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [perm, setPerm] = useState({ canCreate: false, canEdit: false, canDelete: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await fetchAccountingPermissions(axiosInstance, ACCOUNTING_SO_CHI_ADVANCE);
      if (!cancelled) {
        setPerm({
          canCreate: Boolean(p?.canCreate),
          canEdit: Boolean(p?.canEdit),
          canDelete: Boolean(p?.canDelete),
        });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const [disburseRow, setDisburseRow] = useState(null);
  const [disburseBankId, setDisburseBankId] = useState("");
  const [disbursing, setDisbursing] = useState(false);

  const [supplementParent, setSupplementParent] = useState(null);
  const [supplementForm, setSupplementForm] = useState({ amount: "", reason: "" });
  const [supplementSubmitting, setSupplementSubmitting] = useState(false);

  const [settleRow, setSettleRow] = useState(null);
  const [settleForm, setSettleForm] = useState({
    usedAmount: "",
    bankAccountId: "",
    note: "",
  });
  const [settleFiles, setSettleFiles] = useState([]);
  const [settlePreviews, setSettlePreviews] = useState([]);
  const [settling, setSettling] = useState(false);

  const chipDefs = useMemo(() => [
    {
      key: "status",
      label: "Trạng thái",
      isActive: (v) => Boolean(v),
      formatValue: (v) => resolveMapLabel(v, STATUS_MAP),
    },
  ], []);

  const activeChips = useMemo(
    () => buildChips(appliedFilters, chipDefs),
    [appliedFilters, chipDefs]
  );

  const resolveDeptDivision = useCallback((user) => {
    const divIds = Array.isArray(user.divisionIds) && user.divisionIds.length
      ? user.divisionIds.map(Number)
      : user.divisionId != null && user.divisionId !== ""
        ? [Number(user.divisionId)]
        : [];
    let departmentName = "";
    let divisionName = "";
    for (const divId of divIds) {
      for (const d of departments) {
        const div = (d.divisions || []).find((x) => Number(x.id) === Number(divId));
        if (div) {
          departmentName = (d.name || "").trim();
          divisionName = (div.name || "").trim();
          return { departmentName, divisionName };
        }
      }
    }
    return { departmentName, divisionName };
  }, [departments]);

  const memberOptions = useMemo(() => members.map((u) => {
    const code = `NV-${u.id}`;
    const name = u.name || u.username || u.email || code;
    const extra = u.username && u.username !== name ? ` (${u.username})` : "";
    return {
      value: String(u.id),
      label: `${code} — ${name}${extra}`,
      user: u,
    };
  }), [members]);

  const selectedMember = useMemo(
    () => memberOptions.find((o) => o.value === String(form.employeeId)) || null,
    [memberOptions, form.employeeId]
  );

  const fetchLookups = useCallback(async () => {
    try {
      const [usersRes, deptRes, cfgRes, bankRes] = await Promise.all([
        axiosInstance.get("/api/users/searchAdminUsers", { params: { page: 0, size: 1000 } }).catch(() => null),
        axiosInstance.get("/api/users/departments").catch(() => null),
        axiosInstance.get("/api/accounting/salary-configs").catch(() => null),
        axiosInstance.get("/api/accounting/bank-accounts/active").catch(() => null),
      ]);
      const userList = usersRes?.data?.page?.content || usersRes?.data?.content || [];
      setMembers(Array.isArray(userList) ? userList.filter((u) => u.isMember) : []);
      setDepartments(Array.isArray(deptRes?.data) ? deptRes.data : []);
      const cfgList = unwrap(cfgRes);
      setSalaryConfigs(Array.isArray(cfgList) ? cfgList : []);
      const bankData = unwrap(bankRes);
      const banks = Array.isArray(bankData) ? bankData : bankData?.content || [];
      setBankAccounts(banks.filter((b) => b.active !== false));
    } catch {
      /* ignore */
    }
  }, []);

  const fetchData = useCallback(async (p = 0, filters = appliedFilters) => {
    setLoading(true);
    try {
      const params = { page: p, size: PAGE_SIZE };
      if (filters.status) params.status = filters.status;

      const res = await axiosInstance.get("/api/accounting/advances", { params });
      const body = unwrap(res);
      setRows(body.content || []);
      setTotalPages(body.totalPages || 0);
      setTotalElements(body.totalElements || 0);
      setPage(p);
    } catch {
      toast.error("Không thể tải dữ liệu tạm ứng");
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchLookups();
    fetchData(0, EMPTY_ADVANCE_FILTERS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = (next) => {
    setAppliedFilters(next);
    fetchData(0, next);
  };

  const handleSearch = () => applyFilters({ status: filterStatus });

  const handleReset = () => {
    setFilterStatus("");
    applyFilters(EMPTY_ADVANCE_FILTERS);
  };

  const handleRemoveChip = (key) => {
    const base = { ...appliedFilters, [key]: "" };
    if (key === "status") setFilterStatus("");
    applyFilters(base);
  };

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openCreate = () => { setForm(INITIAL_FORM); setShowModal(true); };
  const onFormChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const onSelectEmployee = (opt) => {
    if (!opt) {
      setForm((prev) => ({
        ...prev,
        employeeId: "",
        employeeName: "",
        employeeCode: "",
        departmentName: "",
        divisionName: "",
        position: "",
      }));
      return;
    }
    const u = opt.user;
    const { departmentName, divisionName } = resolveDeptDivision(u);
    const cfg = salaryConfigs.find((c) => Number(c.employeeId) === Number(u.id));
    setForm((prev) => ({
      ...prev,
      employeeId: String(u.id),
      employeeName: u.name || u.username || "",
      employeeCode: `NV-${u.id}`,
      departmentName,
      divisionName,
      position: cfg?.position || "",
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.employeeId || !form.amount) {
      toast.warn("Vui lòng chọn nhân viên và nhập số tiền");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axiosInstance.post("/api/accounting/advances", {
        employeeId: Number(form.employeeId),
        amount: toMoneyNumber(form.amount),
        reason: form.reason,
      });
      if (!isOk(res)) {
        toast.error(res?.data?.message || "Tạo thất bại");
        return;
      }
      toast.success("Tạo đề nghị tạm ứng thành công");
      setShowModal(false);
      fetchData(page, appliedFilters);
    } catch (err) {
      toast.error(err.response?.data?.message || "Tạo thất bại");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAction = async (id, action, label) => {
    const result = await Swal.fire({
      title: `${label}?`,
      icon: action === "reject" ? "warning" : "question",
      showCancelButton: true,
      confirmButtonText: label,
      cancelButtonText: "Huỷ",
      confirmButtonColor: action === "reject" ? "#dc3545" : "#0d6efd",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await axiosInstance.put(`/api/accounting/advances/${id}/${action}`);
      if (!isOk(res)) {
        toast.error(res?.data?.message || `${label} thất bại`);
        return;
      }
      toast.success(`Đã ${label.toLowerCase()}`);
      fetchData(page, appliedFilters);
    } catch (err) {
      toast.error(err.response?.data?.message || `${label} thất bại`);
    }
  };

  const openDisburse = (row) => {
    setDisburseRow(row);
    setDisburseBankId(bankAccounts[0] ? String(bankAccounts[0].id) : "");
  };

  const submitDisburse = async () => {
    if (!disburseRow) return;
    if (!disburseBankId) {
      toast.warn("Vui lòng chọn tài khoản ngân hàng");
      return;
    }
    setDisbursing(true);
    try {
      const res = await axiosInstance.put(`/api/accounting/advances/${disburseRow.id}/disburse`, null, {
        params: { bankAccountId: Number(disburseBankId) },
      });
      if (!isOk(res)) {
        toast.error(res?.data?.message || "Giải ngân thất bại");
        return;
      }
      toast.success("Đã giải ngân");
      setDisburseRow(null);
      fetchData(page, appliedFilters);
    } catch (err) {
      toast.error(err.response?.data?.message || "Giải ngân thất bại");
    } finally {
      setDisbursing(false);
    }
  };

  const openSupplement = (row) => {
    setSupplementParent(row);
    setSupplementForm({ amount: "", reason: "" });
  };

  const submitSupplement = async () => {
    if (!supplementParent) return;
    const amount = Number(supplementForm.amount);
    if (!amount || amount <= 0) {
      toast.warn("Vui lòng nhập số tiền ứng thêm");
      return;
    }
    setSupplementSubmitting(true);
    try {
      const res = await axiosInstance.post(
        `/api/accounting/advances/${supplementParent.id}/supplements`,
        { amount, reason: supplementForm.reason }
      );
      if (!isOk(res)) {
        toast.error(res?.data?.message || "Tạo ứng thêm thất bại");
        return;
      }
      toast.success("Đã tạo phiếu ứng thêm (chờ duyệt)");
      setSupplementParent(null);
      setExpandedIds((prev) => new Set(prev).add(supplementParent.id));
      fetchData(page, appliedFilters);
    } catch (err) {
      toast.error(err.response?.data?.message || "Tạo ứng thêm thất bại");
    } finally {
      setSupplementSubmitting(false);
    }
  };

  const openSettle = (row) => {
    const total = row.totalDisbursedAmount != null ? row.totalDisbursedAmount : row.amount;
    setSettleRow(row);
    setSettleForm({
      usedAmount: total != null ? String(total) : "",
      bankAccountId: bankAccounts[0] ? String(bankAccounts[0].id) : "",
      note: "",
    });
    setSettleFiles([]);
    setSettlePreviews((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.url));
      return [];
    });
  };

  const closeSettle = () => {
    setSettleRow(null);
    setSettlePreviews((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.url));
      return [];
    });
    setSettleFiles([]);
  };

  const handleSettleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    const valid = selected.filter((f) =>
      (f.type || "").startsWith("image/") || f.type === "application/pdf"
    );
    if (valid.length < selected.length) {
      toast.warn("Chỉ chấp nhận ảnh hoặc PDF");
    }
    setSettleFiles((prev) => [...prev, ...valid]);
    setSettlePreviews((prev) => [
      ...prev,
      ...valid.map((f) => ({ name: f.name, url: URL.createObjectURL(f), isPdf: f.type === "application/pdf" })),
    ]);
    e.target.value = "";
  };

  const removeSettlePreview = (index) => {
    setSettlePreviews((prev) => {
      const next = [...prev];
      URL.revokeObjectURL(next[index].url);
      next.splice(index, 1);
      return next;
    });
    setSettleFiles((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
  };

  const disbursedAmount = Number(
    settleRow?.totalDisbursedAmount != null ? settleRow.totalDisbursedAmount : settleRow?.amount || 0
  );
  const usedAmountNum = Number(settleForm.usedAmount || 0);
  const refundPreview = Math.max(0, disbursedAmount - usedAmountNum);
  const overUsed = usedAmountNum > disbursedAmount;

  const submitSettle = async () => {
    if (!settleRow) return;
    if (settleForm.usedAmount === "" || Number.isNaN(usedAmountNum) || usedAmountNum < 0) {
      toast.warn("Vui lòng nhập số tiền thực tế sử dụng");
      return;
    }
    if (overUsed) {
      toast.warn("Số tiền sử dụng vượt tổng đã giải ngân. Hãy tạo phiếu ứng thêm trước.");
      return;
    }
    if (!settleForm.bankAccountId) {
      toast.warn("Vui lòng chọn tài khoản ngân hàng");
      return;
    }
    setSettling(true);
    try {
      let evidencePaths = [];
      if (settleFiles.length > 0) {
        const formData = new FormData();
        settleFiles.forEach((f) => formData.append("files", f));
        const uploadRes = await axiosInstance.post(
          "/api/accounting/advances/upload-evidence",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        if (!isOk(uploadRes)) {
          toast.error(uploadRes?.data?.message || "Upload chứng từ thất bại");
          return;
        }
        evidencePaths = uploadRes.data?.data || [];
      }

      const res = await axiosInstance.post(`/api/accounting/advances/${settleRow.id}/settle`, {
        usedAmount: usedAmountNum,
        bankAccountId: Number(settleForm.bankAccountId),
        note: settleForm.note,
        evidencePaths,
      });
      if (!isOk(res)) {
        toast.error(res?.data?.message || "Tất toán thất bại");
        return;
      }
      toast.success("Đã tất toán tạm ứng");
      closeSettle();
      fetchData(page, appliedFilters);
    } catch (err) {
      toast.error(err.response?.data?.message || "Tất toán thất bại");
    } finally {
      setSettling(false);
    }
  };

  const canSettle = (status) => status === "PENDING_SETTLEMENT" || status === "DISBURSED";
  const canAddSupplement = (status) => canSettle(status);

  const renderActionButtons = (row, { settleAllowed = true } = {}) => (
    <div className="acc-action-btns">
      {perm.canEdit && row.status === "PENDING" && (
        <>
          <Button variant="outline-success" size="sm" title="Duyệt" onClick={() => handleAction(row.id, "approve", "Duyệt")}>
            <FaCheck />
          </Button>
          <Button variant="outline-danger" size="sm" title="Từ chối" onClick={() => handleAction(row.id, "reject", "Từ chối")}>
            <FaTimes />
          </Button>
        </>
      )}
      {perm.canEdit && row.status === "APPROVED" && (
        <Button variant="outline-warning" size="sm" title="Giải ngân" onClick={() => openDisburse(row)}>
          <FaMoneyBillWave />
        </Button>
      )}
      {perm.canEdit && settleAllowed && canAddSupplement(row.status) && !row.parentAdvanceId && (
        <Button variant="outline-secondary" size="sm" title="Ứng thêm" onClick={() => openSupplement(row)}>
          <FaPlus />
        </Button>
      )}
      {perm.canEdit && settleAllowed && canSettle(row.status) && !row.parentAdvanceId && (
        <Button variant="outline-primary" size="sm" title="Tất toán" onClick={() => openSettle(row)}>
          <FaFileInvoiceDollar />
        </Button>
      )}
    </div>
  );

  const body = (
    <div className={embedded ? "" : "acc-page"}>
      <Card className="mb-3 acc-filter-card">
        <Card.Body className="p-0">
          <AccountingFilterSection
            title="Quản lý Tạm ứng"
            open={filterOpen}
            onToggle={setFilterOpen}
            onSubmit={handleSearch}
            onReset={handleReset}
            activeCount={countActiveChips(activeChips)}
            toolbarRight={perm.canCreate && (
              <Button variant="primary" size="sm" onClick={openCreate}>
                <FaPlus className="me-1" /> Tạo đề nghị tạm ứng
              </Button>
            )}
          >
            <Row className="g-2 align-items-end acc-filter-grid">
              <Col>
                <Form.Label className="small mb-1">Trạng thái</Form.Label>
                <Form.Select size="sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                  <option value="">Tất cả</option>
                  {Object.entries(STATUS_MAP)
                    .filter(([k]) => k !== "DISBURSED")
                    .map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                </Form.Select>
              </Col>
            </Row>
          </AccountingFilterSection>
          <AccountingActiveFilters
            chips={activeChips}
            onRemove={handleRemoveChip}
            onClearAll={handleReset}
          />
        </Card.Body>
      </Card>

      <Card style={cardStyle}>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : rows.length === 0 ? (
            <div className="text-center py-5 text-muted">Không có đề nghị tạm ứng nào</div>
          ) : (
            <>
              <div className="acc-table-wrap">
                <Table striped hover className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 36 }} />
                      <th>Mã</th>
                      <th>Nhân viên</th>
                      <th className="text-end">Số tiền</th>
                      <th className="text-end acc-col-hide-sm">Tổng đã GN</th>
                      <th className="acc-col-hide-sm">Lý do</th>
                      <th className="acc-col-hide-sm">Ngày đề nghị</th>
                      <th className="text-center">Trạng thái</th>
                      <th className="text-center">Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const supplements = Array.isArray(r.supplements) ? r.supplements : [];
                      const hasChildren = supplements.length > 0 || (r.supplementCount || 0) > 0;
                      const expanded = expandedIds.has(r.id);
                      return (
                        <Fragment key={r.id}>
                          <tr>
                            <td>
                              {hasChildren ? (
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 text-muted"
                                  onClick={() => toggleExpand(r.id)}
                                  title={expanded ? "Thu gọn" : "Xem ứng thêm"}
                                >
                                  {expanded ? <FaChevronDown /> : <FaChevronRight />}
                                </Button>
                              ) : null}
                            </td>
                            <td className="fw-semibold">
                              {r.code || r.id}
                              {(r.supplementCount || 0) > 0 && (
                                <Badge bg="danger" className="ms-1" title="Số lần ứng thêm">
                                  +{r.supplementCount}
                                </Badge>
                              )}
                            </td>
                            <td>
                              <div>{r.employeeName || "—"}</div>
                              <div className="small text-muted">{r.employeeCode || (r.employeeId ? `NV-${r.employeeId}` : "")}</div>
                            </td>
                            <td className="text-end fw-semibold">{fmtVND(r.amount)}</td>
                            <td className="text-end acc-col-hide-sm">
                              {fmtVND(r.totalDisbursedAmount != null ? r.totalDisbursedAmount : (canSettle(r.status) || r.status === "SETTLED" ? r.amount : 0))}
                            </td>
                            <td className="acc-col-hide-sm">{r.reason || "—"}</td>
                            <td className="acc-col-hide-sm">{r.requestDate || "—"}</td>
                            <td className="text-center">
                              <Badge bg={STATUS_MAP[r.status]?.bg || "secondary"}>
                                {STATUS_MAP[r.status]?.label || r.status}
                              </Badge>
                              {r.paymentVoucherId && (
                                <div className="small mt-1">
                                  <Link to={`/ke-toan/phieu-thu-chi?tab=payment`} className="text-decoration-none">
                                    <FaLink className="me-1" />PC #{r.paymentVoucherId}
                                  </Link>
                                </div>
                              )}
                            </td>
                            <td className="text-center">{renderActionButtons(r)}</td>
                          </tr>
                          {expanded && supplements.map((s) => (
                            <tr key={`supp-${s.id}`} className="table-light">
                              <td />
                              <td className="ps-3">
                                <span className="text-muted small">↳ </span>
                                <span className="fw-semibold">{s.code || s.id}</span>
                                <Badge bg="info" className="ms-1">Ứng thêm</Badge>
                              </td>
                              <td className="small text-muted">—</td>
                              <td className="text-end fw-semibold">{fmtVND(s.amount)}</td>
                              <td className="acc-col-hide-sm text-end text-muted">—</td>
                              <td className="acc-col-hide-sm">{s.reason || "—"}</td>
                              <td className="acc-col-hide-sm">{s.requestDate || "—"}</td>
                              <td className="text-center">
                                <Badge bg={STATUS_MAP[s.status]?.bg || "secondary"}>
                                  {STATUS_MAP[s.status]?.label || s.status}
                                </Badge>
                                {s.paymentVoucherId && (
                                  <div className="small mt-1">
                                    <Link to={`/ke-toan/phieu-thu-chi?tab=payment`} className="text-decoration-none">
                                      <FaLink className="me-1" />PC #{s.paymentVoucherId}
                                    </Link>
                                  </div>
                                )}
                              </td>
                              <td className="text-center">{renderActionButtons(s, { settleAllowed: false })}</td>
                            </tr>
                          ))}
                          {expanded && supplements.length === 0 && (
                            <tr>
                              <td colSpan={9} className="small text-muted ps-4">Chưa có phiếu ứng thêm</td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              <div className="acc-table-footer">
                <small className="text-muted">Tổng: {totalElements} bản ghi</small>
                <Pagination
                  hidePaginationWhenSinglePage
                  showItemsPerPage={false}
                  pageData={{ page: { number: page, totalPages, totalElements, size: PAGE_SIZE } }}
                  onPageChange={(p) => fetchData(p - 1)}
                />
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Create modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton><Modal.Title>Tạo đề nghị tạm ứng</Modal.Title></Modal.Header>
        <Form onSubmit={handleCreate}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Họ và tên (người đề nghị) <span className="text-danger">*</span></Form.Label>
                  <Select
                    options={memberOptions}
                    value={selectedMember}
                    onChange={onSelectEmployee}
                    placeholder="Tìm theo mã NV / họ tên..."
                    isClearable
                    classNamePrefix="react-select"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Mã NV</Form.Label>
                  <Form.Control value={form.employeeCode || ""} disabled readOnly />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Phòng ban</Form.Label>
                  <Form.Control value={form.departmentName || ""} disabled readOnly />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Bộ phận</Form.Label>
                  <Form.Control value={form.divisionName || ""} disabled readOnly />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Chức vụ</Form.Label>
                  <Form.Control value={form.position || ""} disabled readOnly />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Số tiền <span className="text-danger">*</span></Form.Label>
                  <MoneyInput value={form.amount} onValueChange={(v) => onFormChange("amount", v)} placeholder="Nhập số tiền" />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Lý do</Form.Label>
                  <Form.Control as="textarea" rows={3} value={form.reason} onChange={(e) => onFormChange("reason", e.target.value)} placeholder="Lý do tạm ứng..." />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Đóng</Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? <Spinner animation="border" size="sm" /> : "Tạo đề nghị"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Supplement modal */}
      <Modal show={!!supplementParent} onHide={() => setSupplementParent(null)} centered>
        <Modal.Header closeButton><Modal.Title>Ứng thêm tạm ứng</Modal.Title></Modal.Header>
        <Modal.Body>
          {supplementParent && (
            <Row className="g-3">
              <Col md={12}>
                <div className="p-2 rounded border bg-light small">
                  Gắn với phiếu <strong>{supplementParent.code}</strong> — {supplementParent.employeeName}
                  <div>Tổng đã giải ngân: <strong>{fmtVND(supplementParent.totalDisbursedAmount ?? supplementParent.amount)}</strong></div>
                </div>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Số tiền ứng thêm <span className="text-danger">*</span></Form.Label>
                  <MoneyInput
                    value={supplementForm.amount}
                    onValueChange={(v) => setSupplementForm((p) => ({ ...p, amount: v }))}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Lý do</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={supplementForm.reason}
                    onChange={(e) => setSupplementForm((p) => ({ ...p, reason: e.target.value }))}
                    placeholder="Lý do cần ứng thêm..."
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <div className="small text-muted">Phiếu ứng thêm sẽ qua Chờ duyệt → Duyệt → Giải ngân (tạo phiếu chi bổ sung).</div>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSupplementParent(null)}>Huỷ</Button>
          <Button variant="primary" onClick={submitSupplement} disabled={supplementSubmitting}>
            {supplementSubmitting ? <Spinner animation="border" size="sm" /> : "Tạo ứng thêm"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Disburse modal */}
      <Modal show={!!disburseRow} onHide={() => setDisburseRow(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{disburseRow?.parentAdvanceId ? "Giải ngân ứng thêm" : "Giải ngân tạm ứng"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {disburseRow && (
            <>
              <p className="mb-2"><strong>{disburseRow.code}</strong> — {disburseRow.employeeName}</p>
              {disburseRow.parentAdvanceId && (
                <p className="small text-muted mb-2">Ứng thêm cho phiếu gốc #{disburseRow.parentAdvanceId}</p>
              )}
              <p className="mb-3">Số tiền: <strong>{fmtVND(disburseRow.amount)}</strong></p>
              <Form.Group>
                <Form.Label>Tài khoản chi <span className="text-danger">*</span></Form.Label>
                <Form.Select value={disburseBankId} onChange={(e) => setDisburseBankId(e.target.value)}>
                  <option value="">-- Chọn TK --</option>
                  {bankAccounts.map((b) => (
                    <option key={b.id} value={b.id}>
                      {(b.bankName || "") + (b.accountNumber ? ` - ${b.accountNumber}` : "")}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDisburseRow(null)}>Huỷ</Button>
          <Button variant="warning" onClick={submitDisburse} disabled={disbursing}>
            {disbursing ? <Spinner animation="border" size="sm" /> : "Xác nhận giải ngân"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Settle modal */}
      <Modal show={!!settleRow} onHide={closeSettle} centered size="lg">
        <Modal.Header closeButton><Modal.Title>Tất toán tạm ứng</Modal.Title></Modal.Header>
        <Modal.Body>
          {settleRow && (
            <Row className="g-3">
              <Col md={12}>
                <div className="p-2 rounded border bg-light">
                  <div><strong>{settleRow.code}</strong> — {settleRow.employeeName} ({settleRow.employeeCode})</div>
                  <div>Tổng đã giải ngân (gốc + ứng thêm): <strong className="text-primary">{fmtVND(disbursedAmount)}</strong></div>
                  {(settleRow.supplementCount || 0) > 0 && (
                    <div className="small text-muted">{settleRow.supplementCount} phiếu ứng thêm</div>
                  )}
                  {settleRow.paymentVoucherId && (
                    <div className="small">Phiếu chi gốc: #{settleRow.paymentVoucherId}</div>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Số tiền thực tế sử dụng <span className="text-danger">*</span></Form.Label>
                  <MoneyInput
                    value={settleForm.usedAmount}
                    onValueChange={(v) => setSettleForm((p) => ({ ...p, usedAmount: v }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Tài khoản ngân hàng <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={settleForm.bankAccountId}
                    onChange={(e) => setSettleForm((p) => ({ ...p, bankAccountId: e.target.value }))}
                  >
                    <option value="">-- Chọn TK --</option>
                    {bankAccounts.map((b) => (
                      <option key={b.id} value={b.id}>
                        {(b.bankName || "") + (b.accountNumber ? ` - ${b.accountNumber}` : "")}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={12}>
                <div className="small text-muted">Hoàn ứng (tự tính → phiếu thu, vào dòng tiền, không tính doanh thu)</div>
                <div className="fw-semibold text-success">{fmtVND(refundPreview)}</div>
                {overUsed && (
                  <div className="small text-danger mt-1">
                    Vượt tổng đã giải ngân. Hãy dùng nút Ứng thêm, duyệt và giải ngân trước khi tất toán.
                  </div>
                )}
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Ghi chú tất toán</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={settleForm.note}
                    onChange={(e) => setSettleForm((p) => ({ ...p, note: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Chứng từ (ảnh / PDF, nhiều file)</Form.Label>
                  <Form.Control type="file" accept="image/*,application/pdf" multiple onChange={handleSettleFileChange} />
                </Form.Group>
                {settlePreviews.length > 0 && (
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {settlePreviews.map((p, i) => (
                      <div key={i} className="position-relative border rounded p-1">
                        {p.isPdf ? (
                          <div className="d-flex align-items-center justify-content-center" style={{ width: 100, height: 80 }}>
                            <span className="small">{p.name}</span>
                          </div>
                        ) : (
                          <img src={p.url} alt={p.name} style={{ width: 100, height: 80, objectFit: "cover" }} />
                        )}
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0"
                          style={{ padding: "0 4px" }}
                          onClick={() => removeSettlePreview(i)}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeSettle}>Huỷ</Button>
          <Button variant="primary" onClick={submitSettle} disabled={settling || overUsed}>
            {settling ? <Spinner animation="border" size="sm" /> : "Xác nhận tất toán"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );

  if (embedded) return body;
  return <AdLayout>{body}</AdLayout>;
};

export default AdvanceList;
